import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  Facebook, Instagram, Linkedin, Youtube, Film, CheckCircle, XCircle, RefreshCw,
  AlertTriangle, Loader2, Calendar, Play, Zap,
} from 'lucide-react';
import SEO from '@/components/SEO';

const SUPABASE_URL = 'https://plizcqmxjmtzdgmmhtxj.supabase.co';

interface MarketingOutput {
  id: string;
  task_id: string;
  title: string;
  content: string;
  output_type: string;
  status: string;
  created_at: string;
  metadata: {
    batch_id?: string;
    day_index?: number;
    day_label?: string;
    day_theme?: string;
    scheduled_publish_at?: string;
    cta_type?: string;
    violations?: Array<{ platform: string; rule: string; evidence: string }>;
    video_pending?: boolean;
    video_id?: string;
    video_duration_sec?: number;
    manifest_url?: string;
    render_triggered?: boolean;
  } | null;
}

interface QueuePost {
  id: string;
  marketing_output_id: string | null;
  platform: string;
  content: string;
  image_prompt: string | null;
  image_url: string | null;
  status: string;
  scheduled_for: string | null;
  published_at: string | null;
  post_url: string | null;
  source: string | null;
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] as const;
const TEXT_PLATFORMS = ['facebook', 'instagram', 'linkedin'] as const;
const VIDEO_PLATFORMS = ['youtube', 'instagram', 'facebook', 'linkedin_company'] as const;

const PLATFORM_META: Record<string, { icon: typeof Facebook; label: string; color: string }> = {
  facebook: { icon: Facebook, label: 'Facebook', color: 'bg-blue-100 text-blue-700' },
  instagram: { icon: Instagram, label: 'Instagram', color: 'bg-pink-100 text-pink-700' },
  linkedin: { icon: Linkedin, label: 'LinkedIn (Brian)', color: 'bg-amber-100 text-amber-800' },
  linkedin_company: { icon: Linkedin, label: 'LinkedIn (Co)', color: 'bg-sky-100 text-sky-700' },
  youtube: { icon: Youtube, label: 'YouTube', color: 'bg-red-100 text-red-700' },
};

const STATUS_META: Record<string, { label: string; className: string }> = {
  pending: { label: 'Needs Review', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  awaiting_image: { label: 'Awaiting Image', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  approved: { label: 'Approved', className: 'bg-green-100 text-green-800 border-green-300' },
  scheduled: { label: 'Scheduled', className: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
  published: { label: 'Published', className: 'bg-green-600 text-white border-green-700' },
  rejected: { label: 'Rejected', className: 'bg-red-100 text-red-800 border-red-300' },
  failed: { label: 'Failed', className: 'bg-red-100 text-red-800 border-red-300' },
  failed_validation: { label: 'Validation Failed', className: 'bg-red-100 text-red-800 border-red-300' },
  processed: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  queued: { label: 'Queued', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
};

function formatDateTime(iso: string | null): string {
  if (!iso) return 'No schedule';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function WeeklyReview() {
  const [searchParams] = useSearchParams();
  const batchParam = searchParams.get('batch');
  const { toast } = useToast();

  const [batchId, setBatchId] = useState<string | null>(batchParam);
  const [outputs, setOutputs] = useState<MarketingOutput[]>([]);
  const [queuePosts, setQueuePosts] = useState<QueuePost[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [edits, setEdits] = useState<Record<string, string>>({}); // queue post id -> edited content
  const [savingId, setSavingId] = useState<string | null>(null);

  const loadLatestBatch = useCallback(async () => {
    setLoading(true);

    // Resolve batch_id: explicit param > latest batch with pending posts
    let activeBatch = batchId;
    if (!activeBatch) {
      const { data: recent } = await supabase
        .from('marketing_outputs')
        .select('metadata')
        .eq('task_id', 'casey-weekly-batch')
        .order('created_at', { ascending: false })
        .limit(20);
      const bid = (recent || []).map((r: any) => r.metadata?.batch_id).filter(Boolean)[0] || null;
      activeBatch = bid;
      if (bid) setBatchId(bid);
    }

    if (!activeBatch) {
      setOutputs([]);
      setQueuePosts([]);
      setLoading(false);
      return;
    }

    const { data: outs, error: oe } = await supabase
      .from('marketing_outputs')
      .select('*')
      .eq('task_id', 'casey-weekly-batch')
      .filter('metadata->>batch_id', 'eq', activeBatch)
      .order('created_at', { ascending: true });

    if (oe) {
      toast({ title: 'Error loading outputs', description: oe.message, variant: 'destructive' });
      setLoading(false);
      return;
    }

    const outputList = (outs || []) as MarketingOutput[];
    setOutputs(outputList);

    const outputIds = outputList.map(o => o.id);
    if (outputIds.length > 0) {
      const { data: qs } = await supabase
        .from('social_content_queue')
        .select('*')
        .in('marketing_output_id', outputIds)
        .order('platform', { ascending: true });
      setQueuePosts((qs || []) as QueuePost[]);
    } else {
      setQueuePosts([]);
    }

    setLoading(false);
  }, [batchId, toast]);

  useEffect(() => { loadLatestBatch(); }, [loadLatestBatch]);

  // Group queue posts by day_index + platform for the grid
  const grid = useMemo(() => {
    const byOutputId: Record<string, MarketingOutput> = {};
    for (const o of outputs) byOutputId[o.id] = o;

    // Build text grid: 5 days (Mon-Fri) with 3 platforms each
    const textGrid: Record<number, Record<string, QueuePost[]>> = {};
    for (let i = 0; i < 5; i++) textGrid[i] = { facebook: [], instagram: [], linkedin: [] };

    // Video row
    const videoPosts: Record<string, QueuePost[]> = { youtube: [], instagram: [], facebook: [], linkedin_company: [] };

    for (const q of queuePosts) {
      const parent = q.marketing_output_id ? byOutputId[q.marketing_output_id] : null;
      if (!parent) continue;
      const dayIndex = parent.metadata?.day_index;
      const isVideo = parent.output_type === 'video';
      if (isVideo) {
        if (videoPosts[q.platform]) videoPosts[q.platform].push(q);
      } else if (typeof dayIndex === 'number' && dayIndex >= 0 && dayIndex < 5) {
        if (textGrid[dayIndex][q.platform]) textGrid[dayIndex][q.platform].push(q);
      }
    }

    // Also: a map of day -> marketing_output parent (for theme, scheduled time, violations)
    const dayOutputs: Record<number, MarketingOutput | null> = { 0: null, 1: null, 2: null, 3: null, 4: null };
    let videoOutput: MarketingOutput | null = null;
    for (const o of outputs) {
      if (o.output_type === 'video') { videoOutput = o; continue; }
      const di = o.metadata?.day_index;
      if (typeof di === 'number' && di >= 0 && di < 5) dayOutputs[di] = o;
    }

    return { textGrid, videoPosts, dayOutputs, videoOutput };
  }, [outputs, queuePosts]);

  const pendingCount = queuePosts.filter(q => q.status === 'pending' || q.status === 'queued').length;
  const approvedCount = queuePosts.filter(q => q.status === 'approved').length;
  const publishedCount = queuePosts.filter(q => q.status === 'published').length;

  const saveEdit = async (queueId: string) => {
    const newContent = edits[queueId];
    if (newContent === undefined) return;
    setSavingId(queueId);
    const { error } = await supabase
      .from('social_content_queue')
      .update({ content: newContent })
      .eq('id', queueId);
    setSavingId(null);
    if (error) {
      toast({ title: 'Save failed', description: error.message, variant: 'destructive' });
      return;
    }
    setQueuePosts(prev => prev.map(q => q.id === queueId ? { ...q, content: newContent } : q));
    setEdits(prev => { const next = { ...prev }; delete next[queueId]; return next; });
    toast({ title: 'Saved' });
  };

  const updateStatus = async (queueId: string, status: string) => {
    const { error } = await supabase
      .from('social_content_queue')
      .update({ status })
      .eq('id', queueId);
    if (error) {
      toast({ title: 'Update failed', description: error.message, variant: 'destructive' });
      return;
    }
    setQueuePosts(prev => prev.map(q => q.id === queueId ? { ...q, status } : q));
    toast({ title: status === 'approved' ? 'Approved' : `Marked ${status}` });
  };

  const approveAllRemaining = async () => {
    if (!confirm('Approve all pending posts in this week? They will publish on their scheduled dates.')) return;
    const ids = queuePosts.filter(q => q.status === 'pending' || q.status === 'queued').map(q => q.id);
    if (ids.length === 0) { toast({ title: 'Nothing to approve' }); return; }
    const { error } = await supabase.from('social_content_queue').update({ status: 'approved' }).in('id', ids);
    if (error) {
      toast({ title: 'Bulk approve failed', description: error.message, variant: 'destructive' });
      return;
    }
    setQueuePosts(prev => prev.map(q => ids.includes(q.id) ? { ...q, status: 'approved' } : q));
    toast({ title: `Approved ${ids.length} post${ids.length > 1 ? 's' : ''}` });
  };

  const publishNow = async () => {
    setPublishing(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/scheduled-publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      toast({ title: `Published ${data.published || 0}`, description: `Failed: ${data.failed || 0}` });
      await loadLatestBatch();
    } catch (e: any) {
      toast({ title: 'Publish trigger failed', description: e.message, variant: 'destructive' });
    }
    setPublishing(false);
  };

  const generateNewBatch = async () => {
    if (!confirm('Generate a new weekly batch now? This takes ~60 seconds and creates 5 text drafts + 1 video for next week.')) return;
    setGenerating(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/marketing-cron`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_id: 'casey-weekly-batch' }),
      });
      const data = await res.json();
      if (data.ok && data.batch_id) {
        setBatchId(data.batch_id);
        toast({ title: 'New batch ready', description: `${data.outputs_created} days generated, ${data.validation_failures || 0} failures` });
        await loadLatestBatch();
      } else {
        toast({ title: 'Generation failed', description: data.error || 'Unknown error', variant: 'destructive' });
      }
    } catch (e: any) {
      toast({ title: 'Generation error', description: e.message, variant: 'destructive' });
    }
    setGenerating(false);
  };

  return (
    <>
      <SEO title="Weekly Social Review, Admin" noIndex />
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Weekly Social Review</h1>
            <p className="text-muted-foreground text-sm">
              5 text days (FB, IG, LinkedIn) + 1 video (Wed, YT/IG/FB/LI Co). Edit inline, approve, or regenerate.
            </p>
            {batchId && (
              <p className="text-xs text-muted-foreground mt-1">Batch: <code>{batchId.slice(0, 8)}</code></p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-sm">Pending: {pendingCount}</Badge>
            <Badge className="bg-green-100 text-green-800 border-green-300 text-sm">Approved: {approvedCount}</Badge>
            <Badge className="bg-green-600 text-white border-green-700 text-sm">Published: {publishedCount}</Badge>
            <Button onClick={() => loadLatestBatch()} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
            <Button onClick={approveAllRemaining} className="bg-emerald-600 hover:bg-emerald-700" size="sm" disabled={pendingCount === 0}>
              <CheckCircle className="h-4 w-4 mr-1" /> Approve all remaining ({pendingCount})
            </Button>
            <Button onClick={publishNow} variant="outline" size="sm" disabled={publishing || approvedCount === 0}>
              {publishing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
              Publish now
            </Button>
            <Button onClick={generateNewBatch} variant="outline" size="sm" disabled={generating}>
              {generating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
              Generate new batch
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="py-24 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : outputs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground space-y-3">
              <Calendar className="h-10 w-10 mx-auto opacity-40" />
              <p className="text-base">No weekly batch yet.</p>
              <p className="text-sm">Generate the first batch, or wait for Sunday 6pm PT cron.</p>
              <Button onClick={generateNewBatch} disabled={generating}>
                {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                Generate first batch
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Text grid: 5 days × 3 platforms */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {DAY_LABELS.map((dayLabel, dayIdx) => {
                const parent = grid.dayOutputs[dayIdx];
                return (
                  <div key={dayLabel} className="space-y-3">
                    <div className="flex items-center justify-between pb-1 border-b border-border">
                      <div>
                        <h3 className="font-bold text-sm">{dayLabel}</h3>
                        {parent?.metadata?.day_theme && (
                          <p className="text-xs text-muted-foreground">{parent.metadata.day_theme}</p>
                        )}
                      </div>
                      {parent?.metadata?.scheduled_publish_at && (
                        <span className="text-xs text-muted-foreground">
                          {new Date(parent.metadata.scheduled_publish_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      )}
                    </div>
                    {parent?.status === 'failed_validation' && parent.metadata?.violations && (
                      <div className="rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-800 space-y-1">
                        <div className="flex items-center gap-1 font-semibold"><AlertTriangle className="h-3 w-3" /> Validation failed</div>
                        {parent.metadata.violations.slice(0, 3).map((v, i) => (
                          <div key={i}><code>{v.rule}</code>: {v.evidence.substring(0, 40)}</div>
                        ))}
                      </div>
                    )}
                    {TEXT_PLATFORMS.map(plat => {
                      const posts = grid.textGrid[dayIdx]?.[plat] || [];
                      if (posts.length === 0) {
                        return (
                          <Card key={plat} className="border-dashed">
                            <CardContent className="py-3 text-center text-xs text-muted-foreground">
                              No {PLATFORM_META[plat].label} draft
                            </CardContent>
                          </Card>
                        );
                      }
                      return posts.map(p => (
                        <QueuePostCard
                          key={p.id}
                          post={p}
                          editBuffer={edits[p.id]}
                          onEditChange={(val) => setEdits(prev => ({ ...prev, [p.id]: val }))}
                          onSave={() => saveEdit(p.id)}
                          onApprove={() => updateStatus(p.id, 'approved')}
                          onReject={() => updateStatus(p.id, 'rejected')}
                          saving={savingId === p.id}
                        />
                      ));
                    })}
                  </div>
                );
              })}
            </div>

            {/* Video row (Wednesday) */}
            <div className="border-t border-border pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Film className="h-5 w-5 text-purple-600" />
                <h3 className="font-bold text-base">Mid-Week Video (Wed)</h3>
                {grid.videoOutput?.metadata?.video_pending && (
                  <Badge variant="outline" className="text-xs">Generating in background…</Badge>
                )}
                {grid.videoOutput?.metadata?.scheduled_publish_at && (
                  <span className="text-xs text-muted-foreground">
                    {formatDateTime(grid.videoOutput.metadata.scheduled_publish_at)}
                  </span>
                )}
                {grid.videoOutput?.metadata?.render_triggered === false && (
                  <Badge className="bg-orange-100 text-orange-800 border-orange-300 text-xs">Manual render needed</Badge>
                )}
              </div>
              {!grid.videoOutput ? (
                <Card className="border-dashed">
                  <CardContent className="py-3 text-center text-xs text-muted-foreground">No video this week</CardContent>
                </Card>
              ) : (
                <>
                  {grid.videoOutput.metadata?.video_pending && (
                    <Card className="mb-3 bg-muted/50">
                      <CardContent className="py-3 text-sm text-muted-foreground">
                        {grid.videoOutput.content}
                      </CardContent>
                    </Card>
                  )}
                  {grid.videoOutput.metadata?.manifest_url && (
                    <Card className="mb-3 bg-muted/50">
                      <CardContent className="py-3 text-sm text-muted-foreground flex flex-wrap gap-3">
                        <span>Video: <strong>{grid.videoOutput.title}</strong></span>
                        {grid.videoOutput.metadata.video_duration_sec && <span>{grid.videoOutput.metadata.video_duration_sec}s</span>}
                        <a href={grid.videoOutput.metadata.manifest_url} target="_blank" rel="noreferrer" className="text-blue-600 underline">manifest.json</a>
                      </CardContent>
                    </Card>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {VIDEO_PLATFORMS.map(plat => {
                      const posts = grid.videoPosts[plat] || [];
                      if (posts.length === 0) {
                        return (
                          <Card key={plat} className="border-dashed">
                            <CardContent className="py-3 text-center text-xs text-muted-foreground">
                              No {PLATFORM_META[plat].label} draft
                            </CardContent>
                          </Card>
                        );
                      }
                      return posts.map(p => (
                        <QueuePostCard
                          key={p.id}
                          post={p}
                          editBuffer={edits[p.id]}
                          onEditChange={(val) => setEdits(prev => ({ ...prev, [p.id]: val }))}
                          onSave={() => saveEdit(p.id)}
                          onApprove={() => updateStatus(p.id, 'approved')}
                          onReject={() => updateStatus(p.id, 'rejected')}
                          saving={savingId === p.id}
                        />
                      ));
                    })}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Per-post card
// ---------------------------------------------------------------------------

function QueuePostCard({
  post, editBuffer, onEditChange, onSave, onApprove, onReject, saving,
}: {
  post: QueuePost;
  editBuffer: string | undefined;
  onEditChange: (val: string) => void;
  onSave: () => void;
  onApprove: () => void;
  onReject: () => void;
  saving: boolean;
}) {
  const meta = PLATFORM_META[post.platform] || PLATFORM_META.facebook;
  const PIcon = meta.icon;
  const status = STATUS_META[post.status] || STATUS_META.pending;
  const editedValue = editBuffer !== undefined ? editBuffer : post.content;
  const dirty = editBuffer !== undefined && editBuffer !== post.content;
  const isFinal = post.status === 'published' || post.status === 'approved' || post.status === 'rejected';

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ${meta.color}`}>
            <PIcon className="h-3 w-3" /> {meta.label}
          </span>
          <Badge className={`text-xs ${status.className}`}>{status.label}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 pt-0">
        <Textarea
          value={editedValue}
          onChange={(e) => onEditChange(e.target.value)}
          rows={Math.min(10, Math.max(4, Math.ceil(editedValue.length / 50)))}
          className="text-sm font-sans resize-none"
          disabled={isFinal}
        />
        {post.image_url && (
          <img src={post.image_url} alt="preview" className="w-full max-h-40 object-cover rounded" />
        )}
        {post.post_url && (
          <a href={post.post_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline block">View published post</a>
        )}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {dirty && (
            <Button size="sm" onClick={onSave} disabled={saving} className="h-7 text-xs">
              {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
              Save edits
            </Button>
          )}
          {!isFinal && !dirty && (
            <>
              <Button size="sm" onClick={onApprove} className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700">
                <CheckCircle className="h-3 w-3 mr-1" /> Approve
              </Button>
              <Button size="sm" variant="outline" onClick={onReject} className="h-7 text-xs">
                <XCircle className="h-3 w-3 mr-1" /> Reject
              </Button>
            </>
          )}
          {post.scheduled_for && (
            <span className="text-xs text-muted-foreground ml-auto">
              <Calendar className="h-3 w-3 inline mr-1" />
              {formatDateTime(post.scheduled_for)}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
